import { supabase } from '../lib/supabase';

export const wellnessService = {
  /**
   * Get all wellness entries for the current user (for Readiness Index calculations)
   * @returns {Promise<Array>} - Array of all wellness entries
   */
  async getAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      throw new Error('No authenticated user found');
    }

    const { data, error } = await supabase
      ?.from('wellness_entries')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('date', { ascending: false });

    if (error) {
      console.error('Error fetching all wellness entries:', error);
      throw new Error(error?.message || 'Failed to fetch wellness entries');
    }

    return data?.map(entry => ({
      id: entry?.id,
      userId: entry?.user_id,
      date: entry?.date,
      sleep: entry?.sleep,
      fatigue: entry?.fatigue,
      soreness: entry?.soreness,
      stress: entry?.stress,
      hooperIndex: entry?.hooper_index,
      notes: entry?.notes,
      createdAt: entry?.created_at,
      updatedAt: entry?.updated_at
    })) || [];
  },

  // Get wellness entries for a date range
  async getByDateRange(startDate, endDate) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('wellness_entries')?.select('*')?.eq('user_id', user?.id)?.gte('date', startDate)?.lte('date', endDate)?.order('date', { ascending: true });

      if (error) throw error;

      return data?.map(entry => ({
        id: entry?.id,
        userId: entry?.user_id,
        date: entry?.date,
        sleep: entry?.sleep,
        fatigue: entry?.fatigue,
        soreness: entry?.soreness,
        stress: entry?.stress,
        hooperIndex: entry?.hooper_index,
        notes: entry?.notes,
        createdAt: entry?.created_at,
        updatedAt: entry?.updated_at
      }));
    } catch (error) {
      throw new Error(`Failed to fetch wellness entries: ${error.message}`);
    }
  },

  // Get wellness entry for a specific date
  async getByDate(date) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('wellness_entries')?.select('*')?.eq('user_id', user?.id)?.eq('date', date)?.single();

      if (error && error?.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      if (!data) return null;

      return {
        id: data?.id,
        userId: data?.user_id,
        date: data?.date,
        sleep: data?.sleep,
        fatigue: data?.fatigue,
        soreness: data?.soreness,
        stress: data?.stress,
        hooperIndex: data?.hooper_index,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      throw new Error(`Failed to fetch wellness entry: ${error.message}`);
    }
  },

  // Create or update wellness entry
  async createOrUpdate(wellnessData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const dbData = {
        user_id: user?.id,
        date: wellnessData?.date,
        sleep: wellnessData?.sleep,
        fatigue: wellnessData?.fatigue,
        soreness: wellnessData?.soreness,
        stress: wellnessData?.stress,
        notes: wellnessData?.notes || null
      };

      const { data, error } = await supabase?.from('wellness_entries')?.upsert(dbData, { 
          onConflict: 'user_id,date',
          returning: 'minimal'
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        date: data?.date,
        sleep: data?.sleep,
        fatigue: data?.fatigue,
        soreness: data?.soreness,
        stress: data?.stress,
        hooperIndex: data?.hooper_index,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      throw new Error(`Failed to save wellness entry: ${error.message}`);
    }
  },

  // Delete wellness entry
  async delete(id) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('wellness_entries')?.delete()?.eq('id', id)?.eq('user_id', user?.id); // Ensure user can only delete their own entries

      if (error) throw error;
      return true;
    } catch (error) {
      throw new Error(`Failed to delete wellness entry: ${error.message}`);
    }
  },

  // Delete all wellness entries for current user
  async deleteAll() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('wellness_entries')?.delete()?.eq('user_id', user?.id);

      if (error) throw error;
      return true;
    } catch (error) {
      throw new Error(`Failed to delete all wellness entries: ${error.message}`);
    }
  },

  // Import wellness entries from CSV data
  async importFromCSV(csvData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case and add user_id
      const dbData = csvData?.map(entry => ({
        user_id: user?.id,
        date: entry?.date,
        sleep: parseInt(entry?.sleep),
        fatigue: parseInt(entry?.fatigue),
        soreness: parseInt(entry?.soreness),
        stress: parseInt(entry?.stress),
        notes: entry?.notes || null
      }));

      const { data, error } = await supabase?.from('wellness_entries')?.upsert(dbData, { 
          onConflict: 'user_id,date',
          returning: 'minimal'
        })?.select();

      if (error) throw error;

      return data?.map(entry => ({
        id: entry?.id,
        userId: entry?.user_id,
        date: entry?.date,
        sleep: entry?.sleep,
        fatigue: entry?.fatigue,
        soreness: entry?.soreness,
        stress: entry?.stress,
        hooperIndex: entry?.hooper_index,
        notes: entry?.notes,
        createdAt: entry?.created_at,
        updatedAt: entry?.updated_at
      }));
    } catch (error) {
      throw new Error(`Failed to import wellness data: ${error.message}`);
    }
  },

  // Export wellness entries to CSV format
  async exportToCSV() {
    try {
      const entries = await this.getAll();
      
      const csvHeaders = ['Date', 'Sleep', 'Fatigue', 'Soreness', 'Stress', 'Hooper Index', 'Notes'];
      const csvRows = entries?.map(entry => [
        entry?.date,
        entry?.sleep,
        entry?.fatigue,
        entry?.soreness,
        entry?.stress,
        entry?.hooperIndex,
        entry?.notes || ''
      ]);

      const csvContent = [csvHeaders, ...csvRows]?.map(row => row?.map(field => `"${field}"`)?.join(','))?.join('\n');

      return csvContent;
    } catch (error) {
      throw new Error(`Failed to export wellness data: ${error.message}`);
    }
  },

  // Subscribe to real-time changes
  subscribeToChanges(callback) {
    const channel = supabase?.channel('wellness-changes')?.on('postgres_changes', 
          { event: '*', schema: 'public', table: 'wellness_entries' },
          (payload) => callback(payload))?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

export default wellnessService;